import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { formatCurrencyBRL } from '../../../utils/formatCurrencyBRL';
import { formatNeoCoins } from '../../../utils/formatNeoCoins';
import { trackMetaEvent } from '../../../utils/metaPixel';

const LicenseCard = ({ license, onPurchase, onSubscribe, isRecommended: isRecommendedProp }) => {
  const isRecommended = isRecommendedProp ?? license?.isRecommended;
  const iconName = license?.icon || 'FileText';
  const neoCoinsLabel = formatNeoCoins(license?.neo_coins ?? license?.neoCoins);

  return (
    <div
      className={`relative flex flex-col bg-card border rounded-xl p-6 md:p-8
    transition-all duration-300 ease-out transform-gpu
    hover:shadow-elevation-lg md:hover:-translate-y-1 md:hover:scale-[1.02]
        ${isRecommended
          ? 'border-primary/30 ring-1 ring-primary/15 shadow-elevation-md md:hover:shadow-elevation-xl'
          : 'border-border shadow-elevation-sm'
        }`}
    >
      {isRecommended && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-primary text-primary-foreground rounded-full text-xs font-semibold shadow-soft-sm">
          Melhor Escolha
        </div>
      )}

      {/* Header */}
      <div className="text-center mb-6">
        <div
          className={`inline-flex items-center justify-center w-16 h-16 rounded-xl mb-4 ${isRecommended ? 'bg-primary/10' : 'bg-muted/60'
            }`}
        >
          <Icon
            name={iconName}
            size={32}
            className="text-primary"
          />
        </div>

        <h3 className="text-xl md:text-2xl font-bold text-foreground tracking-tight mb-2">
          {license?.license_name}
        </h3>

        <p className="text-sm text-muted-foreground">
          Licença para corretores
        </p>
      </div>

      {/* Price */}
      <div className="text-center mb-6 pb-6 border-b border-border">
        <div className="flex items-baseline justify-center gap-1 mb-1">
          <span className="text-3xl md:text-4xl font-bold text-foreground tracking-tight">
            {formatCurrencyBRL(license?.price)}
          </span>
        </div>
        <p className="text-xs text-muted-foreground">Pagamento Mensal</p>
      </div>

      {/* Features */}
      <div className="space-y-3 mb-6">
        {neoCoinsLabel && (
          <div className="flex items-center gap-2 text-sm rounded-lg bg-amber-500/10 border border-amber-500/20 px-3 py-2">
            <Icon name="Coins" size={16} className="text-amber-500 flex-shrink-0" />
            <span className="text-foreground">
              <strong>{neoCoinsLabel}</strong> Neo Coins incluídas
            </span>
          </div>
        )}

        <div className="flex items-center gap-2 text-sm">
          <Icon name="FileText" size={16} className="text-primary flex-shrink-0" />
          <span className="text-foreground">
            <strong>{license?.max_documents}</strong> gerações de documentos
          </span>
        </div>

        <div className="flex items-center gap-2 text-sm">
          <Icon name="PenTool" size={16} className="text-primary flex-shrink-0" />
          <span className="text-foreground">
            <strong>{license?.max_signatures}</strong> assinaturas de documentos
          </span>
        </div>

        {license?.is_shared && (
          <div className="flex items-center gap-2 text-sm">
            <Icon name="Users" size={16} className="text-primary flex-shrink-0" />
            <span className="text-foreground">
              Permitido compartilhamento com até <strong>{license?.max_members}</strong> usuários
            </span>
          </div>
        )}

        {license?.features?.map((feature, index) => (
          <div key={index} className="flex items-start gap-2 text-sm">
            <Icon
              name="Check"
              size={16}
              className="text-success flex-shrink-0 mt-0.5"
            />
            <span className="text-muted-foreground">{feature}</span>
          </div>
        ))}
      </div>

      {/* CTA */}
      <div className="mt-auto space-y-2">
        <Button
          variant={isRecommended ? 'default' : 'outline'}
          fullWidth
          onClick={() => onPurchase(license)}
          iconName="ShoppingCart"
          iconPosition="left"
        >
          Compra Única
        </Button>

        <Button
          type="button"
          variant="outline"
          fullWidth
          onClick={() => {
            const rawValue = Number(license?.price);
            trackMetaEvent('InitiateCheckout', {
              content_name: license?.license_name || 'Assinatura Neo Modelos',
              content_category: 'subscription',
              currency: 'BRL',
              ...(Number.isFinite(rawValue) ? { value: rawValue } : {}),
            });
            onSubscribe?.(license);
          }}
          iconName="Calendar"
          iconPosition="left"
        >
          Assinar
        </Button>
      </div>
    </div>
  );
};

export default React.memo(LicenseCard);
